import React from "react";
import {Link} from "react-router-dom";
import {
    ArchiveBoxXMarkIcon,
    ArrowUturnLeftIcon,
    BoltIcon,
    CheckCircleIcon,
    ClockIcon,
    InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {InfoTooltip} from "../../../../../components/general/InfoTooltip.tsx";
import {Transaction} from "../../TransactionsTable.tsx";

const PILL_CLASS_NAME = "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-5";
const CLICKABLE_PILL_CLASS_NAME = `${PILL_CLASS_NAME} cursor-pointer transition-colors decoration-1 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;
const PILL_ICON_CLASS_NAME = "size-3.5 shrink-0 opacity-70";
const GRAY_TONE_CLASS_NAME = "bg-gray-100 text-gray-700";
const CLICKABLE_GRAY_TONE_CLASS_NAME = `${GRAY_TONE_CLASS_NAME} hover:bg-gray-200`;

export function automationRuleSettingsPath(ruleId: string): string {
    return `/my-profile?tab=rules&rule_id=${encodeURIComponent(ruleId)}`;
}

interface Props {
    row: Transaction,
    showsBookAwayState: boolean,
    onUndoBookAwayClick?: (transaction: Transaction) => void,
    renderRowActions?: (transaction: Transaction) => React.ReactNode,
}

export function MatchedStatusPill({onClick}: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`${CLICKABLE_PILL_CLASS_NAME} bg-[#DBEDDB] text-[#1C3829] hover:bg-[#C4E2C4]`}
            title="Bekijk match"
            data-testid="gematcht-pill"
        >
            <CheckCircleIcon className={PILL_ICON_CLASS_NAME} />
            <span>Gematcht</span>
        </button>
    );
}

function BookedAwayPill({row, onUndoBookAwayClick}: Pick<Props, "row" | "onUndoBookAwayClick">) {
    return (
        <span
            className={`group ${PILL_CLASS_NAME} ${GRAY_TONE_CLASS_NAME}`}
            data-testid="weggeboekt-tag"
        >
            <ArchiveBoxXMarkIcon className={PILL_ICON_CLASS_NAME} />
            <span>Weggeboekt</span>
            {onUndoBookAwayClick && (
                <button
                    type="button"
                    onClick={() => onUndoBookAwayClick(row)}
                    className="-ml-1.5 -mr-0.5 max-w-0 overflow-hidden rounded text-current opacity-0 transition-all hover:bg-black/10 focus:outline-none focus-visible:ml-0 focus-visible:max-w-4 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40 group-hover:ml-0 group-hover:max-w-4 group-hover:opacity-70 hover:!opacity-100"
                    aria-label="Wegboeken ongedaan maken"
                    title="Wegboeken ongedaan maken"
                >
                    <ArrowUturnLeftIcon className="size-3.5" />
                </button>
            )}
        </span>
    );
}

function VraagpostPill() {
    return (
        <span className={`${PILL_CLASS_NAME} bg-[#FDECC8] text-[#6B4C13]`} data-testid="vraagpost-badge">
            <ClockIcon className={PILL_ICON_CLASS_NAME} />
            <span>Vraagpost</span>
        </span>
    );
}

function AutomationPill({ruleId}: { ruleId: string }) {
    return (
        <Link
            to={automationRuleSettingsPath(ruleId)}
            className={`${CLICKABLE_PILL_CLASS_NAME} ${CLICKABLE_GRAY_TONE_CLASS_NAME}`}
            data-testid="automatisering-pill"
            title="Bekijk automatiseringsregel"
        >
            <BoltIcon className={PILL_ICON_CLASS_NAME} />
            <span>Automatisering</span>
        </Link>
    );
}

function NoMdrPill({tooltip}: { tooltip: string }) {
    return (
        <span data-testid="geen-vraagpost-badge">
            <InfoTooltip text={tooltip} position="top" className="align-middle">
                <span className={`${PILL_CLASS_NAME} ${GRAY_TONE_CLASS_NAME} cursor-help`}>
                    <InformationCircleIcon className={PILL_ICON_CLASS_NAME} />
                    <span>Geen vraagpost</span>
                </span>
            </InfoTooltip>
        </span>
    );
}

function excludedGeneralLedgerAccountTooltip(row: Transaction): string {
    const base = "Geboekt op een grootboekrekening waarvoor geen document nodig is";
    const label = row.generalLedgerAccount?.label_nl;
    return label ? `${base} (${label})` : base;
}

function renderLegacyStatus({row, showsBookAwayState, onUndoBookAwayClick, renderRowActions}: Props): React.ReactNode {
    return (
        <>
            {showsBookAwayState && Boolean(row.matchingExclusionReason) && (
                <BookedAwayPill row={row} onUndoBookAwayClick={onUndoBookAwayClick} />
            )}
            {renderRowActions?.(row)}
        </>
    );
}

function renderStatus(props: Props): React.ReactNode {
    const {row, showsBookAwayState, onUndoBookAwayClick, renderRowActions} = props;
    const mdrStatus = showsBookAwayState ? row.mdrStatus : undefined;

    if (!mdrStatus) {
        return renderLegacyStatus(props);
    }

    if (mdrStatus.isMdr) {
        return <VraagpostPill />;
    }

    switch (mdrStatus.exclusionReason) {
        case "matched":
            return renderRowActions?.(row);
        case "booked_away":
            return <BookedAwayPill row={row} onUndoBookAwayClick={onUndoBookAwayClick} />;
        case "excluded_general_ledger_account":
            return <NoMdrPill tooltip={excludedGeneralLedgerAccountTooltip(row)} />;
        case "tax_authority_iban":
            return <NoMdrPill tooltip="Betaling aan of van de Belastingdienst" />;
        case "automation_rule":
            return mdrStatus.automationRuleId
                ? <AutomationPill ruleId={mdrStatus.automationRuleId} />
                : <NoMdrPill tooltip="Uitgesloten door een automatiseringsregel" />;
        default:
            return renderLegacyStatus(props);
    }
}

export function TransactionStatusCell(props: Props) {
    return (
        <div className="flex w-full items-center justify-start gap-2 pr-3">
            {renderStatus(props)}
        </div>
    );
}
